import type { Dispatch } from 'redux'
import { reset } from 'redux-form'
import { getWatsonConfig, getTimeout, getDelay } from '@ynap/cart-state/src/selectors/getConfig'
import { getLocale } from '@ynap/cart-state'
import { Cart, ResponseError, nativeAppHeaders } from '@ynap/api-utils'
import { dispatch as dispatchEvent } from '@ynap/analytics'
import { timeout, delay } from '@ynap/promise-utils'
import { StoreState } from 'app/state/types'
import { promoSubmittedAnalytics, addPromoSuccessAnalytics, addPromoErrorAnalytics } from './analytics'
import { ADD_PROMOTION_REQUEST, ADD_PROMOTION_SUCCESS, ADD_PROMOTION_ERROR } from '../constants/ActionTypes'
import { getAdditionalCheckoutHeaders } from '../utils/checkoutActionUtils'
import { CheckoutAnalytics } from 'app/types/analytics.types'
import { ShoppingCart } from 'app/types/common-types'

type AddPromotionResponseData = {
    removedPromotionCodes: string[]
    resourceId: string
    orderId: string | number
    shoppingCart: ShoppingCart
    promoCode: string
}

function addPromotionRequest(code: string) {
    return {
        type: ADD_PROMOTION_REQUEST,
        code,
    }
}

function addPromotionSuccess(code: string, payload: AddPromotionResponseData) {
    return {
        type: ADD_PROMOTION_SUCCESS,
        code,
        payload,
    }
}

function addPromotionError(code: string, error: unknown) {
    return {
        type: ADD_PROMOTION_ERROR,
        code,
        error,
    }
}

export default function addPromotion(code: string, checkoutAnalytics: CheckoutAnalytics) {
    return (dispatch: Dispatch, getState: () => StoreState, fetch: WindowOrWorkerGlobalScope['fetch']) => {
        const state = getState()
        const watsonConfig = getWatsonConfig(state)
        const { langId } = getLocale(state)
        const additionalHeaders = {
            ...nativeAppHeaders(state.deviceType),
            ...getAdditionalCheckoutHeaders(state),
        }

        dispatch(addPromotionRequest(code))
        dispatchEvent(promoSubmittedAnalytics(code, checkoutAnalytics))

        const request = Cart.addPromotion({ promotionCode: code, ...watsonConfig, langId, additionalHeaders })

        return delay(timeout(fetch(request), getTimeout(state)), getDelay(state))
            .then((response: Response) => {
                if (!response.ok) {
                    throw new ResponseError(response)
                }
                return response.json()
            })
            .then((data: AddPromotionResponseData) => {
                dispatch(addPromotionSuccess(code, data))
                dispatchEvent(addPromoSuccessAnalytics(code, checkoutAnalytics))
                dispatch(reset('promotion'))
            })
            .catch((error: unknown) => {
                dispatch(addPromotionError(code, error))
                dispatchEvent(addPromoErrorAnalytics(code, checkoutAnalytics))
                throw error
            })
    }
}
